import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import type { Device } from '../../types/api';
import { useBulkDpsEnrollMutation, type DpsEnrollmentResult } from '@store/api/devicesApi';
import { useHasChanged } from '@hooks/useHasChanged';

interface DPSEnrollmentDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDevices: Device[];
  onSuccess?: () => void;
}

const DPSEnrollmentDialog: React.FC<DPSEnrollmentDialogProps> = ({
  open,
  onClose,
  selectedDevices,
  onSuccess,
}) => {
  const [connectionString, setConnectionString] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<DpsEnrollmentResult[]>([]);
  const [summary, setSummary] = useState<{
    total: number;
    successful: number;
    failed: number;
    skipped: number;
  } | null>(null);
  const [error, setError] = useState<string>('');

  const [bulkDpsEnroll] = useBulkDpsEnrollMutation();

  // Reset state when dialog closes
  if (useHasChanged(open) && !open) {
    setConnectionString('');
    setResults([]);
    setSummary(null);
    setError('');
  }

  const handleEnroll = async () => {
    if (!connectionString) {
      setError('Please provide a Connection String');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);
    setSummary(null);

    try {
      const data = await bulkDpsEnroll({
        connection_string: connectionString,
        device_ids: selectedDevices.map((d) => d.deviceId),
      }).unwrap();

      setResults(data.devices || []);
      setSummary({
        total: data.total,
        successful: data.successful,
        failed: data.failed,
        skipped: data.skipped,
      });

      if (data.successful > 0 && onSuccess) {
        // Wait a bit before calling onSuccess to show results
        setTimeout(onSuccess, 1000);
      }
    } catch (err: any) {
      setError(err?.data?.detail || err?.message || 'Failed to enroll devices');
    } finally {
      setLoading(false);
    }
  };

  const getResultIcon = (result: DpsEnrollmentResult) => {
    if (result.success) {
      return <SuccessIcon color="success" />;
    } else if (result.status === 'skipped') {
      return <WarningIcon color="warning" />;
    } else {
      return <ErrorIcon color="error" />;
    }
  };

  const getResultColor = (result: DpsEnrollmentResult) => {
    if (result.success) return 'success';
    if (result.status === 'skipped') return 'warning';
    return 'error';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Enroll Devices in Azure DPS
        {selectedDevices.length > 0 && (
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            {selectedDevices.length} device{selectedDevices.length > 1 ? 's' : ''} selected
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Connection String Input */}
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              Provide the DPS Shared Access Policy connection string, requested from Team
              Rainbow (valid for about one day). Its host determines which DPS environment the
              devices are enrolled into. The connection string will not be stored and is only
              used for this enrollment operation.
            </Typography>
          </Alert>

          <TextField
            label="DPS Connection String"
            value={connectionString}
            onChange={(e) => setConnectionString(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="HostName=xxx.azure-devices-provisioning.net;SharedAccessKeyName=xxx;SharedAccessKey=xxx"
            disabled={loading || results.length > 0}
            helperText="Shared Access Policy connection string with enrollment permissions"
          />

          {/* Error Display */}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Loading Indicator */}
          {loading && (
            <Box>
              <LinearProgress />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 1
                }}>
                Enrolling devices in Azure DPS...
              </Typography>
            </Box>
          )}

          {/* Results Summary */}
          {summary && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Enrollment Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`Total: ${summary.total}`} color="default" />
                <Chip label={`Successful: ${summary.successful}`} color="success" />
                {summary.failed > 0 && <Chip label={`Failed: ${summary.failed}`} color="error" />}
                {summary.skipped > 0 && (
                  <Chip label={`Skipped: ${summary.skipped}`} color="warning" />
                )}
              </Box>
            </Paper>
          )}

          {/* Detailed Results */}
          {results.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Enrollment Details
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {results.map((result, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>{getResultIcon(result)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">{result.device_id}</Typography>
                          <Chip
                            size="small"
                            label={result.status || (result.success ? 'assigned' : 'failed')}
                            color={getResultColor(result)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          {result.error && (
                            <Typography variant="body2" color="error">
                              Error: {result.error}
                            </Typography>
                          )}
                          {result.assigned_hub && (
                            <Typography variant="body2" sx={{
                              color: 'text.secondary'
                            }}>
                              Assigned Hub: {result.assigned_hub}
                            </Typography>
                          )}
                          {result.registration_id && (
                            <Typography variant="body2" sx={{
                              color: 'text.secondary'
                            }}>
                              Registration ID: {result.registration_id}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Selected Devices Preview */}
          {!results.length && selectedDevices.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Devices to Enroll:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                <List dense>
                  {selectedDevices.map((device) => (
                    <ListItem key={device.id} disableGutters>
                      <ListItemText
                        primary={device.name || device.deviceId}
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{
                              display: 'block'
                            }}>
                              ID: {device.deviceId}
                            </Typography>
                            {device.registrationId && (
                              <Typography variant="caption" sx={{
                                display: 'block'
                              }}>
                                Reg ID: {device.registrationId}
                              </Typography>
                            )}
                            {device.customerName && (
                              <Typography variant="caption" sx={{
                                display: 'block'
                              }}>
                                Customer: {device.customerName}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {results.length > 0 ? 'Close' : 'Cancel'}
        </Button>
        {!results.length && (
          <Button
            onClick={handleEnroll}
            variant="contained"
            disabled={loading || !connectionString || selectedDevices.length === 0}
          >
            Enroll Devices
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DPSEnrollmentDialog;
